import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import type {
  ProductType,
  PurchaseWebCampaignOffer,
  PurchaseWebTextValues,
} from '@xing-com/crate-common-graphql-types';
import { Spacer } from '@xing-com/crate-upsell-layouts';
import type { RefinedPremiumContacts } from '@xing-com/crate-upsell-types';
import { mediaWide } from '@xing-com/layout-tokens';
import { spaceL, spaceXL, spaceXXL } from '@xing-com/tokens';

import { useContracts } from '../../hooks/use-contracts';
import { ProfileContactsWithProduct } from '../profile-contacts-with-product';

import { OfferBenefits } from './offer-benefits';
import { OfferBoxCard } from './offer-box-card';
import { OfferBoxHeader } from './offer-box-header';
import { OfferPicker } from './offer-picker';
import { PurchaseButton } from './purchase-button';
import { PurchaseStickyBar } from './purchase-sticky-bar';

export type OfferBoxProps = Pick<
  PurchaseWebCampaignOffer,
  'contracts' | 'endsAt'
> & {
  textValues: PurchaseWebTextValues;
  product: ProductType;
  showFacepile: boolean | undefined;
  premiumContacts?: RefinedPremiumContacts;
  disabled?: boolean;
  showOfferPicker?: boolean;
};

export function OfferBox(props: OfferBoxProps): JSX.Element {
  const {
    contracts,
    endsAt,
    textValues,
    product,
    showFacepile,
    premiumContacts,
    disabled = false,
    showOfferPicker = true,
  } = props;

  const { activeContract, toggleContract } = useContracts(contracts);
  const [ref, isStaticOfferBoxInView] = useInView({
    initialInView: true,
    fallbackInView: true,
  });

  const {
    id,
    orderType,
    initialDuration: duration,
    isBestSeller,
    price,
  } = activeContract;

  return (
    <>
      <div ref={ref}>
        <StyledOfferBoxCard $product={product}>
          <StyledOfferBoxHeader
            product={product}
            textValues={textValues}
            endsAt={endsAt}
            price={price}
          />
          {showFacepile && premiumContacts ? (
            <StyledProfileContactsWithProduct
              renderLocation="offerbox"
              premiumContacts={premiumContacts}
            />
          ) : (
            showOfferPicker && <Spacer $size="spaceS" />
          )}
          {showOfferPicker && (
            <StyledOfferPicker
              isBestSeller={isBestSeller}
              id={id}
              price={price}
              product={product}
              contracts={contracts}
              toggleContract={toggleContract}
            />
          )}
          <StyledOfferBenefits
            benefitsList={textValues.benefits}
            product={product}
          />

          <PurchaseButton
            disabled={disabled}
            id={id}
            orderType={orderType}
            duration={duration}
            product={product}
          />
        </StyledOfferBoxCard>
      </div>
      {!isStaticOfferBoxInView && (
        <PurchaseStickyBar
          showFacepile={showFacepile}
          premiumContacts={premiumContacts}
          isBestSeller={isBestSeller}
          price={price}
          contracts={contracts}
          toggleContract={toggleContract}
          disabled={disabled}
          id={id}
          orderType={orderType}
          duration={duration}
          product={product}
        />
      )}
    </>
  );
}

const StyledOfferBenefits = styled(OfferBenefits)`
  order: 1;

  @media ${mediaWide} {
    order: initial;
  }
`;

const StyledOfferBoxHeader = styled(OfferBoxHeader)`
  margin-bottom: ${spaceXXL};
`;

const StyledProfileContactsWithProduct = styled(ProfileContactsWithProduct)``;

const StyledOfferPicker = styled(OfferPicker)``;

const StyledOfferBoxCard = styled(OfferBoxCard)`
  ${StyledOfferBenefits} {
    padding: ${spaceXXL} 0 0;
  }

  ${StyledProfileContactsWithProduct} {
    margin-bottom: ${spaceXL};
  }

  ${StyledOfferPicker} {
    margin-bottom: ${spaceXL};
  }

  @media ${mediaWide} {
    ${StyledOfferBenefits} {
      padding: 0 0 ${spaceL};
    }
  }
`;
